// Considered: CollapsibleSectionAtom directly (no wrapper) — rejected because every block in a
// rail editor needs the same (icon, anchor, count, caption, completion, ghost tag) header shape
// repeated a dozen times; a thin adapter keeps that shape in one place. Wraps, does not fork, the
// atom. This is the relocated `SphereTypeSectionMolecule` (spec Reuse Audit: `SphereTypeSection-
// Molecule` → extend/relocate): the composer's collapsible block and the rail pattern's
// select-on-click block (`TBlock`, `type-rail/trail-composer.jsx:36-48`; `aspect-rail/rail.css:
// 10-14`, `.t-block-head{cursor:pointer}` / `.t-block[data-sel]`) are one molecule with two head
// behaviours — `collapsible` chooses — so a header fix lands in both consumers at once.

import React from 'react';
import { CollapsibleSectionAtom } from '../../atoms/CollapsibleSection';
import './EditorBlockMolecule.scss';

/** `done` = has content · `ghost` = an agent proposal is waiting · `empty` = untouched. Matches the
 * prototype's index-mark states (`type-composer.jsx:856-862`). */
export type EditorBlockCompletion = 'done' | 'ghost' | 'empty';

export interface EditorBlockMoleculeProps {
  /** Anchor id the section index links to, e.g. `sphere-type-section-identity`. */
  anchorId: string;
  /** PrimeIcons class (`pi pi-tag`) or Iconify name (`lucide:tag`). */
  icon: string;
  label: string;
  /** Item count shown next to the label. Omitted when the block has no countable rows. */
  count?: number;
  /** The contract datum the count renders. */
  countDatum?: string;
  /**
   * The implementation block id (`identity`, `placement`, …) this block renders. Emitted as
   * `data-container` so the parity walker can attribute every datum inside it to a prototype
   * block and check containment and order — PAR-5.
   */
  sectionId?: string;
  /** Right-aligned header slot (e.g. a status chip) — additive to, not replacing, count. */
  rightSlot?: React.ReactNode;
  /** Right-aligned descriptive caption. */
  caption?: string;
  captionDatum?: string;
  /** Explanatory body sentence rendered under the header, above the block's rows. */
  bodyText?: string;
  bodyTextDatum?: string;
  /**
   * `note` (default) is the prototype's `.c-note` treatment — subtle colour, small type —
   * used by Health/Presentation/Languages/Agent guidance's hand-authored body text.
   * `lead` is `.t-derived-lead` — regular text colour, one step larger — used by
   * Relationships/Business rules/Arcs/Perspectives' shared intro sentence
   * (`SECTION_BODY_TEXT_DATUM`). The two are visually distinct in the prototype
   * (`studio.css:652` vs `:1650`), not a single style with two callers.
   */
  bodyTextVariant?: 'note' | 'lead';
  /** Completion mark rendered beside the count. Omitted renders no mark. */
  completion?: EditorBlockCompletion;
  /** An agent proposal is waiting in this block — renders the ghost tag next to the title AND the
   * block-level `data-ghost` attribute (`.t-block[data-ghost]`, `rail.css:204-205`). The tag's copy
   * comes from the host like every other label here (`label`, `caption`, `bodyText`), so this
   * molecule bakes in no editor's i18n namespace. */
  hasGhost?: boolean;
  /** The ghost tag's text (the prototype's `proposed`). Required whenever `hasGhost` is set. */
  ghostTagLabel?: string;
  /** This is the block the rail is currently editing — renders `data-sel` (`.t-block[data-sel]`,
   * `rail.css:11-14`) and the selected header treatment. */
  selected?: boolean;
  /** This block has a validation failure — renders `data-bad` (`.t-block[data-bad]`,
   * `rail.css:206-207`). */
  bad?: boolean;
  /**
   * Text of the tag that names the failure beside the title (the Aspect Editor's `must fix`).
   * The red border alone is colour as the only signal (ux-craft A6), so a host that marks a block
   * `bad` should say so in words. Rendered only while `bad` is set.
   */
  badTagLabel?: string;
  /** Fired when the block's head is clicked — the rail pattern's selection action. Ignored when
   * `collapsible` is set, where the head toggles instead. */
  onSelect?: () => void;
  /**
   * The head toggles the body instead of selecting the block. The rail pattern's `TBlock` never
   * collapses (`aspect-rail/rail.css:10-14`), so this defaults off; the Sphere Type Composer keeps
   * its collapsible document (`SphereTypeComposerOrganism`), and this is the switch that lets one
   * molecule serve both without forking the header.
   *
   * Ignored when `chrome="card"` — the compact card density (spec Reuse Audit: `EditorBlockMolecule`
   * "Extend", `chrome: 'card'`) never collapses; `chrome="card"` forces this off regardless.
   */
  collapsible?: boolean;
  /** Initial open state when `collapsible`. Ignored otherwise — a selecting head is always open. */
  defaultExpanded?: boolean;
  /**
   * `'default'` keeps the existing uppercase collapsible header (forms, the rail's own blocks).
   * `'card'` is the rail-parity doc block: always-expanded, sentence-case title, no collapse
   * affordance (`specs/decompositions/sphere-type-editor-rail.md` block chrome; the prototype's
   * `TBlock`, `type-rail/trail-composer.jsx:36-48`). Additive — omitted behaves exactly as before.
   */
  chrome?: 'default' | 'card';
  children: React.ReactNode;
}

/** Exported so `ComposerIndexRailMolecule` renders the same mark for the same state. */
export const EDITOR_BLOCK_COMPLETION_ICON: Record<EditorBlockCompletion, string> = {
  done: 'pi pi-check-circle',
  ghost: 'pi pi-sparkles',
  empty: 'pi pi-circle',
};

const EditorBlockMolecule: React.FC<EditorBlockMoleculeProps> = ({
  anchorId,
  sectionId,
  icon,
  label,
  count,
  countDatum,
  rightSlot,
  caption,
  captionDatum,
  bodyText,
  bodyTextDatum,
  bodyTextVariant = 'note',
  completion,
  hasGhost,
  ghostTagLabel,
  selected,
  bad,
  badTagLabel,
  onSelect,
  collapsible = false,
  defaultExpanded = true,
  chrome = 'default',
  children,
}) => {
  const isCard = chrome === 'card';
  // Card chrome never collapses — it overrides whatever the caller passed.
  const effectiveCollapsible = isCard ? false : collapsible;
  return (
    <CollapsibleSectionAtom
      // Without this the atom's root never carries `.editor-block`, so every descendant rule in
      // this molecule's stylesheet compiles to a selector that matches nothing.
      className={isCard ? 'editor-block editor-block--card' : 'editor-block'}
      anchorId={anchorId}
      icon={icon}
      title={label}
      collapsible={effectiveCollapsible}
      defaultExpanded={effectiveCollapsible ? defaultExpanded : undefined}
      onHeaderClick={effectiveCollapsible ? undefined : onSelect}
      selected={selected}
      ghost={hasGhost}
      bad={bad}
      data-testid={anchorId}
      data-container={sectionId}
      titleAdornment={
        (hasGhost || (bad && badTagLabel)) && (
          <>
            {hasGhost && (
              <span className="editor-block__ghost-tag" data-testid={`${anchorId}-ghost-tag`}>
                <i className="pi pi-sparkles" aria-hidden="true" />
                {ghostTagLabel}
              </span>
            )}
            {bad && badTagLabel && (
              <span className="editor-block__bad-tag" data-testid={`${anchorId}-bad-tag`}>
                <i className="pi pi-exclamation-triangle" aria-hidden="true" />
                {badTagLabel}
              </span>
            )}
          </>
        )
      }
      headerActions={
        (count !== undefined || rightSlot || caption || completion) && (
          <div className="editor-block__actions">
            {completion && (
              <i
                className={`${EDITOR_BLOCK_COMPLETION_ICON[completion]} editor-block__completion editor-block__completion--${completion}`}
                aria-hidden="true"
                data-testid={`${anchorId}-completion-${completion}`}
              />
            )}
            {count !== undefined && (
              <span className="editor-block__count" data-datum={countDatum} data-kind={countDatum ? 'count' : undefined}>
                {count}
              </span>
            )}
            {caption && (
              <span className="editor-block__caption" data-datum={captionDatum} data-kind={captionDatum ? 'hint' : undefined}>
                {caption}
              </span>
            )}
            {rightSlot}
          </div>
        )
      }
    >
      {bodyText && (
        <p
          className={
            bodyTextVariant === 'lead'
              ? 'editor-block__body-text editor-block__body-text--lead'
              : 'editor-block__body-text'
          }
          data-datum={bodyTextDatum}
          data-kind={bodyTextDatum ? 'note' : undefined}
        >
          {bodyText}
        </p>
      )}
      {children}
    </CollapsibleSectionAtom>
  );
};

EditorBlockMolecule.displayName = 'EditorBlockMolecule';

export default EditorBlockMolecule;
